import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiService } from './apiService';

const defaultAbout = {
  title: 'About Us',
  paragraphs: [
    'Social Worker for Women Empowerment is a non-profit organization dedicated to the upliftment and empowerment of women in rural and urban areas. Founded by Babli Kumari, our NGO works towards providing education, vocational training, healthcare access, and legal support to women who have been marginalized or disadvantaged.',
    'Our vision is to create a society where women are empowered to make their own decisions, pursue their dreams, and contribute equally to the development of their communities. We believe that by investing in women, we invest in the future of our nation.',
    'Through various programs and initiatives, we strive to address issues such as gender inequality, domestic violence, lack of education, and economic dependence. Our team of dedicated volunteers and professionals works tirelessly to bring about positive change in the lives of women across Bihar and beyond.',
  ],
  homeIntro: [
    'Babli Kumari is a dedicated social worker from Muzaffarpur, Bihar, who has been tirelessly working for the empowerment of women in her community and beyond. With a passion for social change and a commitment to equality, she founded the NGO "Social Worker for Women Empowerment" to provide support, education, and resources to women facing various challenges.',
    'Through her initiatives, Babli has helped countless women gain financial independence, access to education, and the confidence to stand up for their rights. Her work focuses on breaking the cycle of poverty and discrimination, empowering women to become leaders in their own lives and communities.',
    'Join us in our mission to create a world where every woman has the opportunity to thrive and succeed.',
  ],
};

function ManageAbout() {
  const [aboutData, setAboutData] = useState(defaultAbout);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      const data = await apiService.getData();
      const about = data?.about || defaultAbout;
      setAboutData({
        title: about.title || defaultAbout.title,
        paragraphs: about.paragraphs?.length ? about.paragraphs : defaultAbout.paragraphs,
        homeIntro: about.homeIntro?.length ? about.homeIntro : defaultAbout.homeIntro,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading about content:', error);
      setMessage('Error loading About page data');
      setLoading(false);
    }
  };

  const handleParagraphChange = (index, value) => {
    setAboutData((prev) => {
      const updated = [...prev.paragraphs];
      updated[index] = value;
      return { ...prev, paragraphs: updated };
    });
  };

  const handleHomeIntroChange = (index, value) => {
    setAboutData((prev) => {
      const updated = [...prev.homeIntro];
      updated[index] = value;
      return { ...prev, homeIntro: updated };
    });
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  const handleSave = async () => {
    try {
      await apiService.updateAbout({
        title: aboutData.title,
        paragraphs: aboutData.paragraphs.filter((paragraph) => paragraph && paragraph.trim()),
        homeIntro: aboutData.homeIntro.filter((paragraph) => paragraph && paragraph.trim()),
      });
      setMessage('About page updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving about page:', error);
      setMessage('Error saving About page');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {message && <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>{message}</Alert>}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">About Page Editor</Typography>
        <Button variant="contained" startIcon={<EditIcon />} onClick={handleSave}>
          Save Changes
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Page Title"
            value={aboutData.title}
            onChange={(e) => setAboutData((prev) => ({ ...prev, title: e.target.value }))}
          />

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Home Page Intro</Typography>
            {aboutData.homeIntro.map((paragraph, index) => (
              <Box key={`home-${index}`} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Intro Paragraph {index + 1}
                </Typography>
                <ReactQuill
                  value={paragraph}
                  onChange={(value) => handleHomeIntroChange(index, value)}
                  modules={modules}
                  theme="snow"
                  style={{ background: '#fff' }}
                />
              </Box>
            ))}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>About Page Content</Typography>
            {aboutData.paragraphs.map((paragraph, index) => (
              <Box key={`about-${index}`} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Paragraph {index + 1}
                </Typography>
                <ReactQuill
                  value={paragraph}
                  onChange={(value) => handleParagraphChange(index, value)}
                  modules={modules}
                  theme="snow"
                  style={{ background: '#fff' }}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ManageAbout;
